import { useContext, useMemo } from "react";
import { useLocation } from "react-router";
import type { z } from "zod";

import { BuilderContext } from "@builder.io/sdk-react";

import { collectBadBuilderValues } from "./carousel-options.logic";
import type { ReportBadBuilderValue } from "./types";

const loggedBadBuilderValues = new Set<string>();

// Parses a block's stored options with a schema built for this block, whose
// reporter collects the bad values of each parse. Each bad value logs once per
// field, problem, entry and block, so bad content does not log every render.
// The entry is in the key because a duplicated Builder entry keeps its blocks'
// ids; the page is only logged, since it comes from the request.
export const useBuilderOptions = <T extends z.ZodTypeAny>(
  createSchema: (report: ReportBadBuilderValue) => T,
  stored: unknown,
  builderBlock: { id?: string } | undefined,
): z.output<T> => {
  const page = useLocation().pathname;
  const entry = useContext(BuilderContext)?.content?.id;
  const { schema, take } = useMemo(() => {
    const collector = collectBadBuilderValues();
    return { schema: createSchema(collector.report), take: collector.take };
  }, [createSchema]);
  const options = schema.parse(stored);
  for (const { field, problem } of take()) {
    const key = `${field}: ${problem} (entry=${entry} block=${builderBlock?.id})`;
    if (loggedBadBuilderValues.has(key)) continue;
    loggedBadBuilderValues.add(key);
    console.error(`[builder-input] page=${page} ${key}`);
  }
  return options;
};
